import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, WarningCircle, Lock, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AdminPasswordResetCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  const [status, setStatus] = useState('processing'); // processing | form | success | error
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const complete = async () => {
      const hash = location.hash || window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;

      const tempToken = sessionStorage.getItem('admin_pwreset_temp_token');

      if (!sessionId) {
        setStatus('error');
        setError('Google verification did not return a session. Please try again.');
        return;
      }
      if (!tempToken) {
        setStatus('error');
        setError('Reset session expired. Please start over.');
        return;
      }

      // Clean URL fragment
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (e) { /* noop */ }

      try {
        const { data } = await axios.post(`${API}/admin/verify-google-for-reset`, {
          session_id: sessionId,
          temp_token: tempToken,
        });

        // Consume the temp_token from storage
        sessionStorage.removeItem('admin_pwreset_temp_token');
        setResetToken(data.reset_token);
        setStatus('form');
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Google verification failed. Please try again.');
      }
    };

    complete();
  }, [location]);

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!newPassword || newPassword.length < 5) {
      setError('Password must be at least 5 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/reset-password-with-token`, {
        reset_token: resetToken,
        new_password: newPassword,
      });
      sessionStorage.removeItem('admin_pwreset_phone');
      setStatus('success');
      setTimeout(() => navigate('/admin-login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-6"
      style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 my-8">
        {status === 'processing' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
              <ShieldCheck size={40} weight="fill" className="text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying with Google...</h2>
            <p className="text-sm text-gray-500">Please wait while we confirm your identity.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        )}

        {status === 'form' && (
          <div>
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
                <CheckCircle size={40} weight="fill" className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Verified successfully</h2>
              <p className="text-sm text-gray-500 mt-1">Now choose a new admin password.</p>
            </div>

            <form onSubmit={submitNewPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 5 chars)"
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="admin-reset-new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="admin-reset-confirm-password"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-wa-hover border border-red-200 text-wa-text-primary px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-reset-submit-btn"
              >
                {submitting ? 'Updating...' : 'Update Password'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Your existing sessions will be signed out automatically.
              </p>
            </form>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
              <CheckCircle size={40} weight="fill" className="text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Password updated!</h2>
            <p className="text-sm text-gray-500">Redirecting to admin login...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
              <WarningCircle size={40} weight="fill" className="text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin-forgot-password', { replace: true })}
              className="w-full py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
              data-testid="admin-reset-retry-btn"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPasswordResetCallback;
